import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { retryPendingLogout, setSessionHint } from '../api/auth';
import { onUnauthorized } from '../api/client';
import { ApiError } from '../api/errors';
import { qk } from '../api/keys';
import { ToastProvider } from '../components/ui/Toast';
import { ThemeProvider } from './theme';

function makeClient() {
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache(),
    mutationCache: new MutationCache(),
    defaultOptions: {
      queries: {
        retry: (count, e) => {
          if (e instanceof ApiError && e.status >= 400 && e.status < 500) return false;
          return count < 2;
        },
        refetchOnWindowFocus: true,
        staleTime: 20_000,
      },
      mutations: { retry: false },
    },
  });
  return client;
}

export function AppProviders({ children }: { children: ReactNode }) {
  const [client] = useState(makeClient);
  useEffect(
    () =>
      onUnauthorized(() => {
        // Сеанс завершився: «я» стає null — оболонка переведе на сторінку входу.
        setSessionHint(false);
        if (client.getQueryData(qk.me)) client.setQueryData(qk.me, null);
      }),
    [client],
  );
  // Вихід, що не дійшов до сервера без мережі, завершуємо за першої нагоди.
  useEffect(() => {
    const retry = () => void retryPendingLogout();
    retry();
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, []);
  return (
    <QueryClientProvider client={client}>
      <ThemeProvider>
        <ToastProvider>{children}</ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
